/**
 * Notification Dropdown Component
 * Story 11.4: Dropdown showing recent notifications
 */

'use client';

import React from 'react';
import Link from 'next/link';
import * as LucideIcons from 'lucide-react';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  getNotificationIcon,
  getNotificationColor,
  formatRelativeTime,
  getResourceLink,
  type Notification,
} from '@/hooks/useNotifications';

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { data, isLoading } = useNotifications(true, 10); // Unread only, limit 10
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      await markAsRead.mutateAsync(notification.id);
    }

    // Navigate to resource if applicable
    const link = getResourceLink(notification.resource_type, notification.resource_id);
    if (link) {
      window.location.href = link;
    }

    onClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.mutateAsync();
  };

  if (isLoading) {
    return (
      <div className="bg-brand-dark-secondary/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-lg p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-white/10 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const notifications = data?.items || [];
  const hasUnread = notifications.some(n => !n.read);

  return (
    <div className="bg-brand-dark-secondary/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          Notifications
        </h3>
        {hasUnread && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-brand-orange hover:text-orange-400 transition-colors"
            disabled={markAllAsRead.isPending}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">
            No new notifications
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/10">
        <Link
          href="/notifications"
          className="text-sm text-brand-orange hover:text-orange-400 transition-colors block text-center"
          onClick={onClose}
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const iconName = getNotificationIcon(notification.notification_type);
  const Icon = (LucideIcons as any)[iconName] || LucideIcons.Bell;
  const colorClass = getNotificationColor(notification.notification_type);

  return (
    <button
      onClick={onClick}
      className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-800 transition-colors text-left ${
        !notification.read ? 'bg-brand-orange/5' : ''
      }`}
    >
      {/* Icon */}
      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
        <Icon size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'} text-white`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-400 line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>

      {/* Unread indicator */}
      {!notification.read && (
        <div className="w-2 h-2 bg-brand-orange rounded-full flex-shrink-0 mt-2" />
      )}
    </button>
  );
}

export default NotificationDropdown;
